import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { FaUserCircle, FaExchangeAlt } from 'react-icons/fa';
import Select from 'react-select';
import moment from 'moment';

const LeadPermissions = ({ leadId, leadData, fetchLeadDetails }) => {
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            // Fetching all Inside Sales users and their ancestors for assignment
            const response = await axios.get(`${backendUrl}/api/users?assign_list=true&department=Sales`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            const usersList = Array.isArray(response.data.users) 
                ? response.data.users 
                : (response.data.users?.data || []);

            const userOptions = usersList
                .filter(u => u.id !== leadData?.assigned_to)
                .map(user => ({
                    value: user.id,
                    label: `${user.name} (${user.roles?.map(r => r.name).join(', ') || 'No Role'})`,
                    email: user.email
                }));

            setUsers(userOptions);
        } catch (error) {
            console.error('Error fetching users:', error);
            setError('Failed to load users for assignment.');
        }
    };

    const handleReassign = async () => {
        if (!selectedUser) {
            setError('Please select a user to assign the lead to.');
            return;
        }

        setLoading(true);
        setError(null);
        setMessage(null);

        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.post(`${backendUrl}/api/leads/${leadId}/reassign`,
                { assigned_to: selectedUser.value },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                }
            );

            setMessage(response.data.message);
            setSelectedUser(null);
            if (fetchLeadDetails) fetchLeadDetails(); // Refresh parent data
            fetchUsers(); // Refresh available users list
        } catch (error) {
            console.error('Error reassigning lead:', error);
            setError(error.response?.data?.message || 'Failed to reassign lead.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="lead-permissions-tab p-3">
            <h5 className="mb-4">Lead Ownership & Permissions</h5>

            {message && <Alert variant="success" onClose={() => setMessage(null)} dismissible>{message}</Alert>}
            {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

            <div className="row">
                <div className="col-md-6">
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Header className="bg-white py-3">
                            <h6 className="mb-0"><FaUserCircle className="me-2 text-primary" /> Owned By</h6>
                        </Card.Header>
                        <Card.Body>
                            <div className="d-flex align-items-center mb-3">
                                <div className="avatar-md bg-light rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '50px', height: '50px' }}>
                                    <FaUserCircle size={30} className="text-secondary" />
                                </div>
                                <div>
                                    <h6 className="mb-1">{leadData?.assigned_user?.name || leadData?.creator?.name || 'Unassigned'}</h6>
                                    <p className="text-muted small mb-0">{leadData?.assigned_user?.email || 'N/A'}</p>
                                    {leadData?.assigned_user?.roles && (
                                        <div className="mt-1">
                                            {leadData.assigned_user.roles.map(role => (
                                                <Badge key={role.id} bg="info" className="me-1">{role.name}</Badge>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <hr />
                            <div className="small text-muted d-flex justify-content-between flex-wrap">
                                <div className="mb-1">
                                    <strong>Created By:</strong> {leadData?.creator?.name || 'N/A'} <span className="ms-1">({leadData?.created_at ? moment(leadData.created_at).format('DD MMM YYYY') : 'N/A'})</span>
                                </div>
                                {leadData?.previous_owner?.name && (
                                    <div className="mb-1">
                                        <strong>Previous Owner:</strong> {leadData.previous_owner.name}
                                    </div>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </div>

                <div className="col-md-6">
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Header className="bg-white py-3">
                            <h6 className="mb-0"><FaExchangeAlt className="me-2 text-primary" /> Transfer Ownership</h6>
                        </Card.Header>
                        <Card.Body>
                            <Form.Group className="mb-4">
                                <Form.Label>Select New Owner</Form.Label>
                                <Select
                                    options={users}
                                    value={selectedUser}
                                    onChange={setSelectedUser}
                                    placeholder="Search for assignment..."
                                    isSearchable
                                />
                                <Form.Text className="text-muted">
                                    Transferring ownership will notify both the new and previous owners.
                                </Form.Text>
                            </Form.Group>

                            <Button
                                variant="primary"
                                className="w-100 d-flex align-items-center justify-content-center"
                                onClick={handleReassign}
                                disabled={loading || !selectedUser}
                            >
                                {loading ? <Spinner animation="border" size="sm" className="me-2" /> : <FaExchangeAlt className="me-2" />}
                                Assign
                            </Button>
                        </Card.Body>
                    </Card>
                </div>
            </div>


        </div>
    );
};

export default LeadPermissions;
